<template>
  <main class="requests-page-container">
    <ReportModal
      v-if="isReportModalActive && $can('read', 'staff_only_items')"
      :is-report-modal-active="isReportModalActive"
      @on-close-modal="toggleReportModal"
      @on-submit="setupReport($event)"
    />
    <div ref="hero" class="hero-container">
      <div class="header-text-column">
        <div class="header-text">
          <h1>{{ headerText }}</h1>
          <NrIconButton
            aria-label="Toggle footer and subheader text"
            variant="minimal-neutral"
            @click="setHeroOpen(!heroOpen)"
            :icon="{
              name: heroOpen ? 'chevron-down' : 'chevron-up',
              type: 'regular'
            }"
          />
        </div>
        <p v-show="heroOpen" v-html="subheader" class="content"></p>
      </div>
      <div
        class="search-container"
        v-if="searchIntegrations && searchIntegrations.display_button"
      >
        <div :class="{ 'limited-search': !heroOpen }">
          <NrTooltip class="search-tooltip" :label="searchIntegrationsTooltip">
            <a
              class="search-link"
              :href="searchIntegrationUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              <NrIcon class="inline-icon" name="external-link-alt" />
              <div>{{ searchIntegrationText }}</div>
            </a>
          </NrTooltip>
        </div>
      </div>
      <div :class="{ 'button-flex': !heroOpen }">
        <div class="button-column">
          <div class="button-container">
            <NrTooltip
              v-if="currentUserPresent && reportPending"
              label="Report generation in progress"
            >
              <NrButton
                variant="bordered-neutral"
                aria-label="Run Reports"
                disabled
                >Run Report</NrButton
              >
            </NrTooltip>

            <NrButton
              v-else-if="$can('read', 'staff_only_items') && !reportPending"
              variant="bordered-neutral"
              aria-label="Run Report"
              @click="isReportModalActive = true"
              >Run report</NrButton
            >
            <NrButton
              v-else-if="
                currentUserPresent &&
                !$can('read', 'staff_only_items') &&
                !reportPending
              "
              @click="setupReport({ report_type: 'csv' })"
              variant="bordered-neutral"
              aria-label="Run Reports"
              >Run Report</NrButton
            >
            <div v-if="showDeleteAllRequests" class="delete-button">
              <NrButton
                variant="filled-danger"
                aria-label="Delete all requests"
                @click="toggleDeleteAllReqModal"
                >Delete all requests</NrButton
              >
              <NrModal
                title="Delete all requests"
                hasSecondaryButton
                primaryButtonText="Delete"
                secondaryButtonText="Cancel"
                :show="isDeleteAllReqsModalActive"
                @on-primary-click="
                  deleteAllRequests(), toggleDeleteAllReqModal()
                "
                @on-secondary-click="toggleDeleteAllReqModal"
              >
                <template>
                  {{ deleteAllReqsModalBody }}
                </template>
              </NrModal>
            </div>
          </div>
        </div>
        <div :class="{ 'pagination-wrapper': heroOpen }">
          <NrPagination
            aria-next-label="Next page"
            aria-previous-label="Previous page"
            aria-page-label="Page"
            aria-current-label="Current page"
            perPage="100"
            :total="paginationCount"
            :current="filters.page_number"
            @change="handlePaginationChange"
          />
        </div>
      </div>
    </div>

    <div class="request-body has-background-white-bis" ref="mainContent">
      <div v-show="showFilter" class="request-sidefilter">
        <SideFilters
          :not-guest="$can('read', 'staff_only_items')"
          :current-user="$store.state.currentUser"
          :preview-user-id="$store.state.previewUserId"
          :account="$store.state.account"
          :filters-height="`${
            isSmallScreen() ? 'auto' : mainContentScrollableHeight
          }`"
          :heroOpen="heroOpen"
          :device="device"
          :sidebarHasMinWidth="sidebarHasMinWidth"
          @toggle-filter="toggleFilter"
          @apply-filters="applyFilters"
          @onSearchRequestFieldFocus="setSearchRequestFieldFocus(true)"
          @onSearchRequestFieldFocusOut="setSearchRequestFieldFocus(false)"
        />
      </div>
      <div class="table-container" id="skip-to-main-content">
        <RequestsTable
          :height="mainContentScrollableHeight"
          @sort="setTableSort"
        />
      </div>
    </div>
    <Footer
      ref="footer"
      class="footer-mobile"
      v-show="heroOpen"
      :show-footer="setShowFooter()"
      :account="$store.getters.account"
      :useStaffHelpLink="canReadStaffOnly"
    />
  </main>
</template>

<script>
import { isEmpty, debounce, isEqual } from 'lodash'
import inflection from 'inflection'
import { mapActions, mapMutations, mapState } from 'vuex'
import NrModal from '@nextrequestco/components/src/components/NrModal/nr-modal.vue'
import SideFilters from './SideFilters.vue'
import ReportModal from './ReportModal.vue'
import Footer from '../../../vue_shared/components/Footer.vue'
import { isMobile, isTablet } from 'mobile-device-detect'
import setupApplyFilterKeyEventListeners from '../../../mixins/setupApplyFilterKeyEventListeners'
import removeApplyFilterKeyEventListeners from '../../../mixins/removeApplyFilterKeyEventListeners'
import togglePageScroll from '../../../mixins/toggle-page-scroll.ts'
import NrIcon from '@nextrequestco/components/src/components/NrIcon/NrIcon.vue'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton/nr-icon-button.vue'
import NrButton from '@nextrequestco/components/src/components/NrButton/nr-button'
import NrPagination from '@nextrequestco/components/src/components/NrPagination/nr-pagination.vue'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import { parse } from '../../../vue_shared/query_params.ts'
import { parseFilterParams } from '../store/parse-filter-params.ts'
import RequestsTable from './RequestsTable.vue'
import NrSentry from '../../../utils/nr_sentry'

export default {
  name: 'RequestPage',
  mixins: [
    setupApplyFilterKeyEventListeners,
    removeApplyFilterKeyEventListeners,
    togglePageScroll
  ],
  components: {
    SideFilters,
    NrModal,
    ReportModal,
    NrIcon,
    NrIconButton,
    NrButton,
    Footer,
    RequestsTable,
    NrPagination,
    NrTooltip
  },
  data() {
    return {
      isSimple: false,
      windowWidth: 0,
      mainContentScrollableHeight: 300,
      device: null,
      dropdownFilter: true,
      isDeleteAllReqsModalActive: false,
      isReportModalActive: false,
      deleteAllReqsModalBody:
        'You are about to delete ALL requests from your portal. This will delete every request, including ' +
        'all sample requests and any requests you created.',
      keysPressed: {},
      applyFilterEventListener: () => {},
      resetKeysEventListener: () => {},
      searchIntegrationsTooltip: 'Search tooltip',
      searchRequestFieldFocus: false
    }
  },

  mounted() {
    this.setupHeroForMobile()
    this.setMainContentHeight()
    this.fetchPendingReportJob()
    this.setActiveSavedView()
    this.getSearchIntegrations()
  },

  created() {
    this.setupApplyFilterKeyEventListeners()
    this.setMainContentHeight = debounce(this.setMainContentHeight, 300)
    window.addEventListener('resize', this.setMainContentHeight)
  },

  unmounted() {
    this.removeApplyFilterKeyEventListeners()

    window.removeEventListener('resize', this.setMainContentHeight)
  },

  watch: {
    heroOpen() {
      this.setMainContentHeight()
    },

    $route(next, prev) {
      const prevParams = parseFilterParams(parse(prev.query))
      const nextParams = parseFilterParams(parse(next.query))

      if (!isEqual(prevParams, nextParams)) {
        this.fetchRequests()
          .catch(() => {
            this.$toasted.error('An error occured. Please try again.')
          })
          .finally(() => {
            if (this.requests.length) {
              this.configureRequestsTableColumns(this.$ability)
            }
            this.togglePageScroll()
          })
      }
    }
  },

  computed: {
    ...mapState('requests', [
      'allRequestsCount',
      'columns',
      'currentPageNumber',
      'filters',
      'heroOpen',
      'requests',
      'resetSortIcons',
      'savedViews',
      'searchResultsCount',
      'showFilter',
      'tableHeaderTitle',
      'tableHeaderTitleTooltip',
      'reportPending',
      'searchIntegrations',
      'filters'
    ]),

    ...mapState({
      account: (state) => state.account,
      subheader: (state) => {
        // all_requests_page_subheader is sanitized on the backend:
        // https://github.com/nextrequest/nextrequest/blob/83b5a5/app/views/client/accounts/account.json.jbuilder#L43
        return (
          (state.account && state.account.all_requests_page_subheader) ||
          'Use filters to find the requests you’re looking for.'
        )
      }
    }),

    showDeleteAllRequests() {
      const validAccountStates = [
        'onboarding',
        'sandbox',
        'unconfirmed',
        'unverified'
      ]

      return (
        this.account &&
        validAccountStates.includes(this.account.state) &&
        this.requests.length > 0 &&
        this.$ability.can('read', 'staff_only_items')
      )
    },

    headerText() {
      return `Explore ${this.allRequestsCount} ${inflection.inflect(
        'request',
        this.allRequestsCount
      )}`
    },

    searchIntegrationText() {
      return this.searchIntegrations?.button_text || ''
    },

    searchIntegrationUrl() {
      let currentUrl = ''
      const { searchIntegrations, filters } = this

      if (!searchIntegrations) return currentUrl
      const url = searchIntegrations.url
      currentUrl = url.trim().replace(/\/*$/, '').trimEnd()
      const searchPhrase = encodeURIComponent(filters?.search_term || '')
      if (searchPhrase === '') return currentUrl

      const { products, integrated_product: integratedProduct } =
        searchIntegrations

      switch (integratedProduct) {
        case products.municode_codification:
          currentUrl = `${currentUrl}&searchText=${searchPhrase}`
          break
        case products.municipal_websites_central:
          currentUrl = `${currentUrl}/Search?searchPhrase=${searchPhrase}`
          break
        case products.municipal_website_open:
          currentUrl = `${currentUrl}/search/site/${searchPhrase}`
          break
        case products.agenda_meeting_management:
          currentUrl = `${currentUrl}/search?q=${searchPhrase}`
          break
        case products.social_media_archiving:
          currentUrl = `${currentUrl}/results?query.terms[0].field=TEXT&query.terms[0].text=${searchPhrase}`
          break
        default:
          break
      }
      return currentUrl
    },

    sidebarHasMinWidth() {
      return isMobile && !isTablet
    },
    canReadStaffOnly() {
      return this.$can('read', 'staff_only_items')
    },
    currentUserPresent() {
      return isEmpty(this.$store.state.currentUser) === false
    },
    paginationCount() {
      return this.searchResultsCount > 9950 ? 9950 : this.searchResultsCount
    },
    pageNum: {
      set(pageNum) {
        this.setTablePage(pageNum)
      },

      get() {
        return this.currentPageNumber
      }
    }
  },

  methods: {
    ...mapActions('requests', [
      'deleteAllRequests',
      'fetchAbilityRequests',
      'fetchAccount',
      'fetchClosureResponse',
      'fetchTag',
      'fetchRequests',
      'setCurrentPageNumber',
      'toggleFilter',
      'setIsLoading',
      'generateReport',
      'pollReportJob',
      'setResetSortIcons',
      'setTableColumnsInCookie',
      'setHeroOpen',
      'setFilters',
      'fetchSavedView',
      'configureRequestsTableColumns',
      'fetchOptionsFromFilterState',
      'fetchSearchIntegrations'
    ]),

    ...mapMutations('requests', [
      'setSelectedSavedView',
      'setFilterOptions',
      'setReportPendingStatus'
    ]),

    setSearchRequestFieldFocus(val) {
      this.searchRequestFieldFocus = val
    },

    setupHeroForMobile() {
      if (isMobile) {
        this.device = 'isMobile'
        this.isSimple = true
        this.setHeroOpen(false)
        this.toggleFilter()
      } else if (isTablet) {
        this.device = 'isTable'
      }
    },

    setActiveSavedView() {
      this.fetchSavedView()
        .then(() => {
          const saveViewId = this.$cookies.get('savedView')
          if (!saveViewId) {
            return
          }

          const activeSavedView = this.savedViews.find(
            (fil) => fil.id.toString() === saveViewId
          )
          if (!activeSavedView) {
            return
          }
          this.setSelectedSavedView(activeSavedView)
          this.setFilters(activeSavedView.filters)
        })
        .finally(async () => {
          await this.applyFilterParams(this.$route.query)

          this.fetchOptionsFromFilterState()

          // NOTE: Returning this promise to handle the failed request to trigger
          // the chaining catch below.
          return this.fetchRequests().finally(() => {
            if (this.requests.length) {
              this.configureRequestsTableColumns(this.$ability)
            }
            this.togglePageScroll()
          })
        })
        .catch((err) => {
          this.$toasted.error('An error occured. Please try again.')
          NrSentry.captureError(err)
        })
    },

    applyFilterParams(queryParams) {
      this.$store.dispatch('requests/applyFilters', queryParams)
    },

    handlePaginationChange(pageNumber) {
      this.setFilters({ page_number: pageNumber })
      this.applyFilterParams()
    },

    setShowFooter() {
      if (isMobile || isTablet) {
        return false
      }
    },

    ifTablet() {
      if (isTablet) {
        return 'subheader-tablet'
      }
    },

    fetchPendingReportJob() {
      let pendingReports = localStorage.getItem('pendingReports')
      if (!pendingReports) {
        return
      }
      pendingReports = JSON.parse(pendingReports)
      if (pendingReports.length) {
        const jobIds = pendingReports.map((rep) => rep.job_id)
        this.setReportPendingStatus(true)
        this.pollReportJob(jobIds)
      }
    },

    toggleDeleteAllReqModal() {
      this.isDeleteAllReqsModalActive = !this.isDeleteAllReqsModalActive
    },

    toggleReportModal() {
      this.isReportModalActive = false
    },

    applyFilters() {
      this.applyFilterParams()
    },

    setTableSort(sort) {
      this.setFilters({
        sort_field: sort.field,
        sort_order: sort.order
      })
      this.applyFilterParams()
    },

    // Use debounce with a delay to prevent double clicks
    setupReport: debounce(function (payload) {
      this.toggleReportModal()
      this.generateReport(payload)
    }, 1000),

    customSortFieldForPrettyId(sort) {
      if (sort.field === 'pretty_id') {
        sort.field = 'pretty_id' // eslint-disable-line no-param-reassign
      }
      return sort
    },

    applyFilterShortcut(e) {
      this.keysPressed[e.key] = true
      this.triggerApplyFilterShortcut(e)
    },

    triggerApplyFilterShortcut(e) {
      if (
        this.keysPressed.Control &&
        document.activeElement.id !== 'request-search-field'
      ) {
        document.activeElement.blur() // Removes focus on the active element to avoid unchecking a filter when clicking ctrl+enter
      }

      if (
        (this.keysPressed.Control && e.key === 'Enter') ||
        (e.key === 'Enter' && this.searchRequestFieldFocus)
      ) {
        this.applyFilters()
      }
    },

    resetKeysPressed(e) {
      delete this.keysPressed[e.key]
    },

    setMainContentHeight() {
      if (!this.$refs) {
        return
      }

      if (!this.$refs.hero && !this.$refs.footer) {
        return
      }

      const appHeader = document.querySelector('#app-header')
      if (!appHeader) {
        return
      }
      const headerHeight = appHeader.getBoundingClientRect().height

      const heroHeight = this.$refs.hero.getBoundingClientRect().height
      const footerHeight = this.$refs.footer.$el.getBoundingClientRect().height
      const tableResultMeta = this.$el.querySelector('.requests-table-header')
      const metaHeight = tableResultMeta.getBoundingClientRect().height

      this.mainContentScrollableHeight =
        window.innerHeight -
        heroHeight -
        footerHeight -
        headerHeight -
        metaHeight

      const previewBanner = document.querySelector('div.preview-banner')
      if (previewBanner) {
        this.mainContentScrollableHeight -=
          previewBanner.getBoundingClientRect().height
      }
    },

    getSearchIntegrations() {
      this.fetchSearchIntegrations().then(() => {
        this.searchIntegrationsTooltip =
          this.searchIntegrations?.tooltip_text || ''
      })
    }
  }
}
</script>

<style scoped lang="scss">
@import '@nextrequestco/components/src/styles/tokens/tokens.scss';

.requests-page-container {
  display: flex;
  flex-flow: column;
  min-width: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
  .hero-container {
    display: flex;
    flex-wrap: wrap;
    padding-left: $spacing-12;
    padding-right: $spacing-12;
    padding-top: $spacing-20;
    padding-bottom: $spacing-20;
    .header-text-column {
      margin-bottom: $spacing-4;
      margin-right: auto;
      width: auto;
      @media (max-width: 780px) {
        width: 100%;
      }
      .header-text {
        display: flex;
        h1 {
          font-weight: $font-weight-semibold;
          font-size: $font-size-5;
        }
        .nr-button {
          margin-left: $spacing-8;
        }
      }
    }
    .button-flex {
      display: flex;
      flex-wrap: wrap;
      align-content: center;
      .button-column {
        margin-right: $spacing-16;
      }
    }
    .button-column {
      @media (max-width: 515px) {
        margin-bottom: $spacing-8;
      }
      .button-container {
        display: flex;
        flex-direction: row-reverse;
        @media (max-width: 515px) {
          display: block;
        }
        &.button-wrap {
          flex-wrap: wrap;
        }
        .delete-button {
          margin-right: $spacing-16;
          @media (max-width: 515px) {
            margin-top: $spacing-8;
          }
        }
      }
    }
    .pagination-wrapper {
      display: flex;
      flex-direction: row-reverse;
      @media (max-width: 515px) {
        margin-top: unset;
        flex-direction: row;
      }
      margin-top: $spacing-24;
    }
    .search-container {
      display: flex;
      justify-content: center;
      align-self: flex-end;
      .search-tooltip {
        .search-link {
          display: flex;
          justify-content: center;
          align-items: center;
          flex-wrap: wrap;
          gap: $spacing-8;
          padding: $spacing-4 $spacing-16;
          margin-right: $spacing-8;
          border: 1px solid transparent;
          border-radius: $spacing-4;
          color: $color-blue-100;
          font-weight: 600;
          height: 34px;

          &:focus {
            color: $color-blue-100;
            border: 1px solid $color-blue-100;
            cursor: pointer;
          }

          &:hover {
            color: $color-blue-80;
            cursor: pointer;
          }

          .disabled-link {
            opacity: 0.2;
          }
        }
      }
    }
  }
  .request-body {
    display: flex;
    @media (max-width: 512px) {
      flex-wrap: wrap;
    }
    .request-sidefilter {
      min-width: 375px;
      @media (max-width: 512px) {
        flex-grow: 2;
      }
    }
    .table-container {
      flex-grow: 2;
    }
  }
  .limited-search {
    height: $spacing-36;
  }
  .footer {
    position: absolute;
    bottom: 0;
    width: 100vw;
    z-index: 1;
  }
}
</style>
